import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, from, Observable, Subscription, zip } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import {
  Channel,
  ChannelAPIResponse,
  MessageResponse,
  SendMessageAPIResponse,
  StreamChat,
} from 'stream-chat';
import { IChat, IChatOptions } from './chat.data';
import { noop } from 'lodash';

@Injectable()
export class ChatService {
  /**
   * main stream chat
   */
  private _chat: StreamChat;
  /**
   * To maintain active channel
   */
  private _activeChannel: Channel;
  /**
   * Storing initial messages and new messages
   */
  private _messages: IChat[];
  /**
   * Publish whatever messages variable has
   */
  private _messages$: BehaviorSubject<IChat[]> = new BehaviorSubject<IChat[]>(
    []
  );

  constructor(private _ngZone: NgZone) {}

  init(chatOptions: IChatOptions): Observable<any> {
    const {
      apiKey,
      userId,
      userName,
      userToken,
      channelId,
      channelName,
    } = chatOptions;

    this._ngZone.runOutsideAngular(() => {
      this._chat = new StreamChat(apiKey);
    });

    return zip(
      this._connectUser(userId, userName, userToken),
      this._joinChannel(channelId, channelName)
    );
  }

  sendMessage(text: string): Observable<SendMessageAPIResponse> {
    return this._ngZone.runOutsideAngular(() =>
      from(this._activeChannel.sendMessage({ text }))
    );
  }

  getMessages(): Observable<IChat[]> {
    return this._messages$.asObservable();
  }

  destroy(): Subscription {
    return this._ngZone.runOutsideAngular(() =>
      from(this._activeChannel.stopWatching())
        .pipe(take(1))
        .subscribe(noop, noop)
    );
  }

  private _connectUser(userId: string, username: string, userToken: string) {
    return this._ngZone.runOutsideAngular(() => {
      return from(
        this._chat.connectUser(
          {
            id: userId,
            name: username,
          },
          userToken
        )
      );
    });
  }

  private _joinChannel(
    channelId: string,
    channelName: string
  ): Observable<ChannelAPIResponse> {
    return this._ngZone.runOutsideAngular(() => {
      this._activeChannel = this._chat.channel('messaging', channelId, {
        name: channelName,
      });

      return from(this._activeChannel.watch()).pipe(
        tap(() => {
          this._getInitialMessages();
          this._listenNewMessage();
        })
      );
    });
  }

  /**
   * Listen to new message of the channel, it initializes web socket
   */
  private _listenNewMessage(): void {
    this._ngZone.runOutsideAngular(() => {
      this._activeChannel.on('message.new', (event) => {
        const newMessage = this._convertMessage(event.message);
        this._messages = this._messages.concat(newMessage);

        // Run this inside Angular so change detection is executed
        this._ngZone.run(() => {
          this._messages$.next(this._messages);
        });
      });
    });
  }

  /**
   * Get existing messages from the channel
   */
  private _getInitialMessages(): void {
    this._ngZone.runOutsideAngular(() => {
      this._messages = this._activeChannel.state.messages.map((message) =>
        this._convertMessage(message as any)
      ) as any;
    });

    this._messages$.next(this._messages);
  }

  private _convertMessage(message: MessageResponse): IChat {
    const {
      created_at,
      user: { name },
      text,
    } = message;

    return {
      date: created_at,
      name,
      message: text,
    };
  }
}
